import {
  ClientProvider,
  Geofence,
  Invoice,
  Module,
  ReferencePoint,
  SafeZone,
  User,
  Vehicle,
} from "@/interfaces";
import api from "@/lib/axios";
import { DefaultGetRequestParams, DefaultResponse } from "./types";

interface GetVehiclesParamsType {
  platform_id?: number;
  summarized?: boolean;
  group?: string;
}

interface GeographicAreasResponse {
  errors: boolean;
  data: {
    reference_points: ReferencePoint[];
    safe_zones: SafeZone[];
    geofences: Geofence[];
  };
}

interface UserInput {
  user_data: {
    password?: string | null;
    type: number;
    name: string;
    email?: string | null;
    user: string | null;
    drivers_license?: string | null;
    drivers_license_expiration?: string | null;
    drivers_license_category?: number | null;
    phone?: string | null;
    status: number;
    serial?: string | null;
    ibutton?: string | null;
    enable_notifications: boolean | number;
    toxicological_exam_date?: string | null;
    medical_exam_date?: string | null;
    access_level_id?: number | null;
  };
  vehicles?: { vehicle_id: number; permission: number }[];
  modules?: { module_id: number; permission: number }[];
  permissionsToDrive?: { vehicle_id: number; status: number }[];
}

export interface VehiclePermission {
  permission: number;
  authorization_to_drive: number;
  authorization_to_drive_label: string;
  title: string;
  plate: string;
}

export interface ModulePermission {
  permission: number;
  module_title: string;
  parent_id: number;
}

export interface UserNotifications {
  id: string;
  type: string;
  data: {
    message: string;
    action_url: string;
    action_text: string;
  };
  notifiable_id: number;
  notifiable_type: string;
  read_at: string | null;
  created_at: string;
}

export const userService = {
  async getVehicles(
    userId: number,
    { platform_id, summarized, group }: GetVehiclesParamsType = {},
  ) {
    const response = await api.get<DefaultResponse<Vehicle>>(
      `/user/${userId}/vehicle`,
      {
        params: {
          platform_id: platform_id ?? 1,
          summarized: summarized ?? 0,
          group: group ?? null,
        },
      },
    );
    return response.data;
  },

  async getPaginatedVehicles(
    userId: number,
    {
      platform_id,
      page,
      per_page,
      search,
    }: {
      platform_id?: number;
      page?: number;
      per_page?: number;
      search?: string;
    } = {},
  ) {
    const response = await api.get<DefaultResponse<Vehicle>>(
      `/user/${userId}/vehicle`,
      {
        params: {
          platform_id: platform_id ?? 1,
          page: page ?? 1,
          per_page: per_page ?? 10,
          search: search,
        },
      },
    );
    return response.data;
  },

  async index({
    page,
    per_page,
    search,
    filters = {},
  }: DefaultGetRequestParams) {
    const response = await api.get<DefaultResponse<User>>("/user/non_drivers", {
      params: {
        page: page,
        per_page: per_page,
        search: search,
        ...filters,
      },
    });
    return response.data;
  },

  async destroy(id: number) {
    const response = await api.delete(`/user/${id}`);
    return response.data;
  },

  async show(id: number) {
    const response = await api.get<DefaultResponse<User, false>>(`/user/${id}`);
    return response.data;
  },

  async update(id: number | string, data: UserInput) {
    const response = await api.put(`/user/${id}`, {
      ...data.user_data,
      vehicles: data.vehicles,
      modules: data.modules,
      permissions_to_drive: data.permissionsToDrive,
    });
    return response.data;
  },

  async create(data: UserInput) {
    const response = await api.post("/user", {
      ...data.user_data,
      vehicles: data.vehicles,
      modules: data.modules,
      permissions_to_drive: data.permissionsToDrive,
    });
    return response.data;
  },

  async getDrivers(userId: number) {
    const response = await api.get<DefaultResponse<User>>(
      `/user/${userId}/driver`,
    );
    return response.data;
  },

  async getUsers() {
    const response = await api.get<DefaultResponse<User>>(`/user`);
    return response.data;
  },

  async getInvoices(userId: number) {
    const response = await api.get<DefaultResponse<Invoice>>(
      `/user/${userId}/invoice`,
    );
    return response.data;
  },

  async getVehiclePermissions(userId: number) {
    const response = await api.get<DefaultResponse<VehiclePermission>>(
      `/user/${userId}/vehicles_permission`,
    );
    return response.data;
  },

  async getModulePermissions(userId: number, platform: number) {
    const response = await api.get<DefaultResponse<ModulePermission>>(
      `/user/${userId}/modules_permission?platform_id=${platform}`,
    );
    return response.data;
  },

  async getModules(userId: number, platform_id?: number) {
    let params = {};
    if (platform_id) {
      params = { platform_id };
    }
    const response = await api.get<DefaultResponse<Module>>(
      `/user/${userId}/module`,
      {
        params,
      },
    );
    return response.data;
  },

  async getClientProviders(userId: number) {
    const response = await api.get<DefaultResponse<ClientProvider>>(
      `/user/${userId}/provider`,
    );
    return response.data;
  },

  async getFuelProviders(userId: number) {
    const response = await api.get<DefaultResponse<ClientProvider>>(
      `/user/${userId}/fuel_provider`,
    );
    return response.data;
  },

  async getGeographicAreas(userId: number, to_load: string) {
    const params = { to_load };
    const response = await api.get<GeographicAreasResponse>(
      `/user/${userId}/get_geographic_areas`,
      { params },
    );

    return response.data;
  },

  async getUserGeofences({
    user_id,
    page,
    per_page,
    search,
    filters = {},
  }: DefaultGetRequestParams & { user_id: number | string }) {
    const response = await api.get<DefaultResponse<Geofence>>(
      `/user/${user_id}/geofence`,
      {
        params: {
          page: page,
          per_page: per_page,
          search: search,
          ...filters,
        },
      },
    );
    return response.data;
  },

  async setAlarms(alarmsData: { vehicle_id: number; alarm_status: number }[]) {
    const response = await api.put(`/user/set_alarms`, {
      alarms: alarmsData,
    });
    return response.data;
  },

  async addImage(id: number, formData: FormData) {
    const response = await api.post(`/user/${id}/image`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  async deleteImage(id: number) {
    const response = await api.delete(`/user/${id}/image`);
    return response.data;
  },

  async getUserNotifications(userId: number) {
    const response = await api.get<{ data: UserNotifications[] }>(
      `/user/${userId}/notification?unread=1`,
    );
    return response.data?.data ?? [];
  },

  async setNotificationAsRead(id?: string) {
    const response = await api.post(`/notification/read`, { id });
    return response.data;
  },

  async registerPushToken(token: string) {
    const response = await api.post(`/user/register_push_token`, {
      token,
    });
    return response.data;
  },
};
